import React from 'react';
import { X, AlertCircle, AlertTriangle, Info } from 'lucide-react';

interface ErrorPopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'error' | 'warning' | 'info';
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    disabled?: boolean;
  }[];
  showCloseButton?: boolean;
}

export default function ErrorPopup({
  isOpen,
  onClose,
  title,
  message,
  type = 'error',
  actions = [],
  showCloseButton = true
}: ErrorPopupProps) {
  if (!isOpen) return null;

  const typeConfig = {
    error: {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-600',
      iconColor: 'text-red-500'
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-600',
      iconColor: 'text-orange-500'
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-600',
      iconColor: 'text-blue-500'
    }
  };

  const variantClasses = {
    primary: 'bg-gray-800 hover:bg-gray-900 text-white',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    danger: 'bg-red-500 hover:bg-red-600 text-white'
  };

  const config = typeConfig[type];
  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-8 w-8 ${config.bgColor} ${config.borderColor} border rounded-full flex items-center justify-center`}>
              <IconComponent className={`h-5 w-5 ${config.iconColor}`} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        
        <p className="text-gray-600 mb-6">{message}</p>
        
        {actions.length > 0 && (
          <div className="flex gap-3 justify-end">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                disabled={action.disabled}
                className={`font-medium py-2 px-4 rounded-md transition-colors text-sm ${
                  variantClasses[action.variant || 'primary']
                } ${action.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 